<template>
	<div
		id="all_expenses"
		:class="{
			'contentBlock marginBottomSmall': isDisabled,
			contentBlock: !isDisabled
		}"
		role="form"
		aria-label="Expenses Form"
	>
		<!-- Advertising Expenses -->
		<div class="contentBlock marginBottomSmaller">
			<ExpenseInput
				:expense="advertisingExpense"
				:is-disabled="isDisabled"
				@update="handleExpenseUpdate"
			/>
		</div>

		<!-- Moving Expenses -->
		<div class="contentBlock marginBottomSmaller">
			<ExpenseInput
				:expense="movingExpense"
				:is-disabled="isDisabled"
				@update="handleExpenseUpdate"
			/>
		</div>

		<!-- Setup Expenses -->
		<div class="contentBlock marginBottomSmaller">
			<ExpenseInput
				:expense="setupExpense"
				:is-disabled="isDisabled"
				@update="handleExpenseUpdate"
			/>
		</div>

		<!-- GIA Expenses -->
		<div class="contentBlock marginBottomSmaller">
			<ExpenseInput
				:expense="giaExpense"
				:is-disabled="isDisabled"
				@update="handleExpenseUpdate"
			/>
		</div>

		<!-- Custom Expenses -->
		<div
			v-for="expense in customExpenses"
			:key="expense.id"
			class="contentBlock marginBottomSmaller"
		>
			<ExpenseInput
				:expense="expense"
				:is-disabled="isDisabled"
				@update="handleCustomExpenseUpdate"
			/>
		</div>
	</div>
</template>

<script>
	import contractState from '@/mixins/contractState'
	import { mapMutations } from 'vuex'
	import { UPDATE_EXPENSES } from '@/store/contract/constants'
	import ExpenseInput from '@/components/Contract/components/ExpenseInput.vue'

	export default {
		name: 'Expenses',
		components: {
			ExpenseInput
		},
		mixins: [contractState],
		props: {
			contract: {
				type: Object,
				required: true
			}
		},
		data() {
			const findExpense = (type) => this.contract?.expenses?.find((e) => e.type === type) || {}
			const customExpenses = this.contract?.expenses?.filter((e) => e.type === 'other') || []

			return {
				pendingUpdates: {},
				advertisingExpense: {
					...findExpense('advertising'),
					description: 'Advertising Expenses',
					type: 'advertising',
					contract_id: this.contract?.id
				},
				movingExpense: {
					...findExpense('moving'),
					description: 'Moving Expenses',
					type: 'moving',
					contract_id: this.contract?.id
				},
				setupExpense: {
					...findExpense('setup'),
					description: 'Setup Expenses',
					type: 'setup',
					contract_id: this.contract?.id
				},
				giaExpense: {
					...findExpense('gia'),
					description: 'Gemstone ID and Grading Expenses',
					type: 'gia',
					contract_id: this.contract?.id
				},
				customExpenses: customExpenses.map((expense) => ({
					...expense,
					contract_id: this.contract?.id
				}))
			}
		},
		computed: {
			/**
			 * Whether the expenses are disabled
			 * @returns {boolean} True if the expenses are disabled, false otherwise
			 */
			isDisabled() {
				return !this.isStaff || !this.isEditable()
			}
		},
		mounted() {
			if (!this.isDisabled && this.customExpenses.length === 0) {
				this.addCustomExpense()
			}
		},
		methods: {
			...mapMutations({
				mutateExpenses: UPDATE_EXPENSES,
				setContractData: 'SET_CONTRACT_DATA'
			}),
			/**
			 * Handle expense update
			 * @param {Object} updatedExpense - The updated expense
			 */
			handleExpenseUpdate(updatedExpense) {
				// Add to pending updates
				this.pendingUpdates[updatedExpense.type] = updatedExpense

				// Update local state based on expense type
				switch (updatedExpense.type) {
					case 'advertising':
						this.advertisingExpense = { ...updatedExpense }
						break
					case 'moving':
						this.movingExpense = { ...updatedExpense }
						break
					case 'setup':
						this.setupExpense = { ...updatedExpense }
						break
					case 'gia':
						this.giaExpense = { ...updatedExpense }
						break
				}

				// Debounce the update to allow multiple changes
				if (this._updateTimeout) {
					clearTimeout(this._updateTimeout)
				}

				this._updateTimeout = setTimeout(() => {
					this.updateExpenses(this.pendingUpdates)
					this.pendingUpdates = {} // Clear pending updates
				}, 300) // Wait 300ms for more changes
			},
			/**
			 * Add a new custom expense
			 */
			addCustomExpense() {
				this.customExpenses.push({
					id: Date.now(), // Temporary ID until saved
					contract_id: this.contract.id,
					description: '',
					amount: '',
					type: 'other'
				})
			},
			/**
			 * Get all custom expenses that have values
			 * @returns {Object[]} Array of custom expenses that have values
			 */
			getAllCustomExpenses() {
				// Filter out any empty expenses
				return this.customExpenses.filter(
					(expense) => expense.description.trim() !== '' && expense.amount.trim() !== ''
				)
			},
			/**
			 * Handle updates to custom expenses
			 */
			handleCustomExpenseUpdate(updatedExpense) {
				// Add to pending updates using the ID as the key
				this.pendingUpdates[`custom-${updatedExpense.id}`] = updatedExpense

				// Update the local custom expenses array
				const index = this.customExpenses.findIndex((e) => e.id === updatedExpense.id)
				if (index >= 0) {
					this.$set(this.customExpenses, index, updatedExpense)
				}

				// Debounce the update
				if (this._updateTimeout) {
					clearTimeout(this._updateTimeout)
				}

				this._updateTimeout = setTimeout(() => {
					this.updateExpenses(this.pendingUpdates)
					this.pendingUpdates = {} // Clear pending updates
					this.checkExpenseInputsForValues()
				}, 300)
			},
			/**
			 * Check if all custom expenses have values
			 * If so, add a new expense
			 */
			checkExpenseInputsForValues() {
				// Store active element id before any DOM updates
				const activeElementId = document.activeElement?.id

				// If all current expenses have values, add a new one
				const allFilled = this.customExpenses.every(
					(expense) => expense.description.trim() !== '' && expense.amount.trim() !== ''
				)

				if (allFilled) {
					// Add new expense
					this.addCustomExpense()

					// Use nextTick to wait for DOM update
					this.$nextTick(() => {
						// Restore focus if needed
						if (activeElementId) {
							document.getElementById(activeElementId)?.focus()
						}
					})
				}
			},
			/**
			 * Update the expenses in the contract
			 * @param {Object} update - The update to apply to the expenses
			 */
			updateExpenses(update) {
				try {
					// Start with current contract expenses or empty array
					let expenses = []

					// Add standard expenses that have values
					;[
						this.advertisingExpense,
						this.movingExpense,
						this.setupExpense,
						this.giaExpense
					].forEach((expense) => {
						if (expense.amount || expense.description) {
							const pendingUpdate = update[expense.type]
							expenses.push({
								...expense,
								...(pendingUpdate || {}),
								contract_id: this.contract?.id
							})
						}
					})

					// Add custom expenses that have values
					const validCustomExpenses = this.getAllCustomExpenses()
					validCustomExpenses.forEach((expense) => {
						const pendingUpdate = update[`custom-${expense.id}`]
						expenses.push({
							...expense,
							...(pendingUpdate || {}),
							contract_id: this.contract?.id
						})
					})

					this.mutateExpenses(expenses)

					// Update contract in store with new expenses
					const updatedContract = {
						...this.contract,
						expenses
					}
					this.setContractData(updatedContract)
				} catch (error) {
					console.error('Failed to update expenses:', error)
				}
			}
		}
	}
</script>

<style scoped></style>
